/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
  useTransitionStyles,
} from '@floating-ui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { tokens } from '@neo4j-ndl/base';
import classNames from 'classnames';
import type React from 'react';
import {
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { type CommonProps } from '../_common/types';
import { ConditionalWrap } from '../conditional-wrap';
import { useIsInsideDialog } from '../dialog/dialog-context';
import { ExclamationCircleIconSolid } from '../icons';
import { useNeedleTheme } from '../theme';
import { Typography } from '../typography';
import {
  formatTimeZone,
  generateTimeZoneOptions,
  generateUTCTimeZoneOptions,
  isValidUTCFormat,
  parseCustomUTCOffset,
} from './generate-timezone-options';
import {
  useKeyboardNavigation,
  useTimeZonePickerPopover,
} from './timezone-picker-hooks';

export type TimeZonePickerProps = {
  /** The value of the timezone picker (IANA timezone identifier) */
  value?: string;
  /** Whether the timezone picker is fluid */
  isFluid?: boolean;
  /** Whether the timezone picker is read only */
  isReadOnly?: boolean;
  /** Whether the timezone picker is required */
  isRequired?: boolean;
  /** Whether the timezone picker is disabled */
  isDisabled?: boolean;
  /** The label of the timezone picker */
  label?: string;
  /** The size of the timezone picker */
  size?: 'small' | 'medium' | 'large';
  /** Manually set the error text */
  errorText?: string;
  /** Callback function triggered when the timezone picker value changes */
  onChange?: (timezone: string) => void;
  /** Callback function triggered when the timezone picker encounters an error */
  onError?: (error: string, timezone: string) => void;
  /**
   * Strategy for the dropdown floating element.
   * By default it is absolute, but if the component is inside of a needle Dialog, it is set to fixed.
   * If this prop is set, no auto-detection of Dialog is done.
   */
  floatingStrategy?: 'absolute' | 'fixed';
  /** Whether the dropdown element is rendered in a portal. */
  isPortaled?: boolean;
  /** Reference date for calculating timezone offsets (useful for DST-aware offset display) */
  referenceDate?: Date;
  /**
   * Display mode: 'city', 'utc', or 'both'
   * 'city' - shows city-based timezones, This should be used when the user needs to select a timezone for themselves or if they are selecting a timezone that's related to a date.
   * 'utc' - shows UTC timezones, This should be used when the user needs to select a timezone for a database entry or a technical application.
   * 'both' - shows both city-based and UTC timezones in separate sections, This should be used when the user needs to select a timezone for a database entry or a technical application, but also wants to see the city-based timezones for context for example a zoned date time.
   * @default 'city'
   * @example
   * <TimeZonePicker mode="city" />
   * <TimeZonePicker mode="utc" />
   * <TimeZonePicker mode="both" />
   */
  mode?: 'city' | 'utc' | 'both';
};

export const TimeZonePicker = ({
  isDisabled,
  isFluid,
  isReadOnly,
  isRequired,
  value,
  label,
  onChange,
  className,
  onError,
  style,
  size = 'medium',
  errorText,
  htmlAttributes,
  floatingStrategy,
  isPortaled: isPortaledProp,
  referenceDate,
  mode = 'city',
  ref,
  ...restProps
}: CommonProps<'input', TimeZonePickerProps>) => {
  const [inputValue, setInputValue] = useState<string>(
    value
      ? formatTimeZone(value, referenceDate)
      : formatTimeZone(
          new Date().toISOString().split('T')[1].split('.')[0],
          referenceDate,
        ),
  );
  const [searchQuery, setSearchQuery] = useState<string>('');
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, []);
  const [error, setError] = useState<string | undefined>(undefined);
  const errorTextId = useId();
  const listboxId = useId();

  const timezoneOptions = useMemo(() => {
    if (mode === 'utc') {
      return generateUTCTimeZoneOptions(referenceDate);
    } else if (mode === 'city') {
      return generateTimeZoneOptions(referenceDate);
    } else {
      // 'both' mode - combine both lists, excluding UTC from city list to avoid duplication
      return [
        ...generateUTCTimeZoneOptions(referenceDate),
        ...generateTimeZoneOptions(referenceDate, true), // excludeUTC = true
      ];
    }
  }, [referenceDate, mode]);

  const { themeClassName } = useNeedleTheme();
  const { isPopoverOpen, setIsPopoverOpen } = useTimeZonePickerPopover(false);

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery) {
      return timezoneOptions;
    }

    const query = searchQuery.toLowerCase();
    return timezoneOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(query) ||
        option.value.toLowerCase().includes(query),
    );
  }, [timezoneOptions, searchQuery]);

  // Split options into UTC and city-based sections for 'both' mode
  const { utcOptions, cityOptions } = useMemo(() => {
    if (mode !== 'both') {
      return { cityOptions: filteredOptions, utcOptions: [] };
    }

    const utc: typeof filteredOptions = [];
    const city: typeof filteredOptions = [];

    filteredOptions.forEach((option) => {
      // UTC options are those that start with "UTC" in their value or label
      if (
        option.value.toUpperCase().startsWith('UTC') ||
        option.label.toUpperCase().startsWith('UTC')
      ) {
        utc.push(option);
      } else {
        city.push(option);
      }
    });

    return { cityOptions: city, utcOptions: utc };
  }, [filteredOptions, mode]);

  const { focusedIndex, setFocusedIndex, handleArrowNavigation } =
    useKeyboardNavigation(filteredOptions.length);

  const errorToShow =
    errorText !== undefined && errorText !== '' ? errorText : error;

  const classes = {
    'ndl-disabled': isDisabled,
    'ndl-error': errorToShow !== undefined,
    'ndl-fluid': isFluid,
    'ndl-large': size === 'large',
    'ndl-medium': size === 'medium',
    'ndl-read-only': isReadOnly,
    'ndl-small': size === 'small',
  };

  const inputWidth = isFluid === true ? '100%' : '16rem';

  useEffect(() => {
    if (error !== undefined) {
      onError?.(error, inputValue);
    }
  }, [error, inputValue, onError]);

  useEffect(() => {
    setInputValue(value ? formatTimeZone(value, referenceDate) : '');
  }, [value, referenceDate]);

  useEffect(() => {
    if (isPopoverOpen) {
      setTimeout(() => {
        listRef.current?.children[focusedIndex]?.scrollIntoView({
          block: 'center',
          inline: 'nearest',
        });
      }, 0);
    } else {
      setTimeout(() => {
        inputRef.current?.blur();
        const selectedIndex =
          filteredOptions.findIndex((opt) => opt.value === value) ?? 0;
        setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
        setSearchQuery('');
      }, 0);
    }
  }, [focusedIndex, isPopoverOpen, value, setFocusedIndex, filteredOptions]);

  const handleOnClick = (timezone: string) => {
    if (onChange) {
      onChange(timezone);
    }
    setError(undefined);
    setInputValue(formatTimeZone(timezone, referenceDate));
    setIsPopoverOpen(false);
    setSearchQuery('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setInputValue(query);

    // In UTC mode (utc or both), validate custom UTC input
    if (
      (mode === 'utc' || mode === 'both') &&
      query.trim().toUpperCase().startsWith('UTC')
    ) {
      if (isValidUTCFormat(query.trim())) {
        setError(undefined);
      } else {
        setError('Invalid UTC format. Use UTC+H:MM or UTC-H:MM');
      }
      return;
    }

    // Try to find a matching timezone
    const matchingOption = timezoneOptions.find(
      (opt) =>
        opt.label.toLowerCase().includes(query.toLowerCase()) ||
        opt.value.toLowerCase().includes(query.toLowerCase()),
    );

    if (matchingOption) {
      setError(undefined);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isReadOnly === true || isDisabled === true) {
      e.preventDefault();
      return;
    }

    if (e.key === 'Escape') {
      setIsPopoverOpen(false);
      setSearchQuery('');
      setInputValue(value ? formatTimeZone(value, referenceDate) : '');
    }

    setIsPopoverOpen(true);

    if (e.key === 'Enter') {
      // In UTC mode (utc or both), allow custom UTC input
      if (
        (mode === 'utc' || mode === 'both') &&
        inputValue.trim().toUpperCase().startsWith('UTC')
      ) {
        const parsedOffset = parseCustomUTCOffset(inputValue.trim());
        if (parsedOffset) {
          handleOnClick(parsedOffset);
          return;
        }
      }

      if (filteredOptions.length > 0) {
        const selectedOption = filteredOptions[focusedIndex];
        if (selectedOption) {
          handleOnClick(selectedOption.value);
        }
      }
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      handleArrowNavigation(e.key);
    }
  };

  /** Custom floating ui solution */
  const isInsideDialog = useIsInsideDialog();
  const isPortaled = isPortaledProp ?? !isInsideDialog;
  const containerRef = useRef<HTMLDivElement>(null);
  const { refs, floatingStyles, context } = useFloating({
    elements: {
      reference: containerRef.current,
    },
    middleware: [
      offset(10),
      flip({
        fallbackPlacements: ['top'],
        fallbackStrategy: 'bestFit',
      }),
      shift({ padding: 5 }),
    ],
    onOpenChange: (open) => {
      setIsPopoverOpen(open);
    },
    open: isDisabled !== true && isReadOnly !== true && isPopoverOpen,
    placement: 'bottom',
    strategy: floatingStrategy ?? (isInsideDialog ? 'fixed' : 'absolute'),
    whileElementsMounted: autoUpdate,
  });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);
  const { styles: transitionStyles } = useTransitionStyles(context, {
    duration: Number.parseInt(tokens.motion.duration.quick) ?? 0,
  });

  const mergedRef = useMergeRefs([refs.setReference, containerRef]);

  return (
    <>
      <div
        className={classNames('ndl-timezone-picker', className, {
          ...classes,
        })}
        ref={mergedRef}
        {...getReferenceProps()}
        style={{
          width: isFluid === true ? '100%' : 'fit-content',
        }}
      >
        <label className="ndl-timezone-picker-label">
          {label !== undefined && (
            <Typography
              variant={size === 'large' ? 'body-large' : 'body-medium'}
            >
              {label}
            </Typography>
          )}
          <div
            className="ndl-timezone-picker-input-wrapper"
            style={{
              width: inputWidth,
              ...style,
            }}
          >
            <input
              role="combobox"
              aria-expanded={isPopoverOpen}
              aria-controls={listboxId}
              aria-activedescendant={
                isPopoverOpen && focusedIndex >= 0
                  ? `${listboxId}-option-${focusedIndex}`
                  : undefined
              }
              aria-label={label}
              aria-invalid={Boolean(errorToShow)}
              aria-describedby={errorToShow ? errorTextId : undefined}
              className="ndl-timezone-picker-input"
              type="text"
              ref={inputRef}
              value={inputValue}
              disabled={isDisabled}
              readOnly={isReadOnly}
              required={isRequired}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onClick={(e) => {
                if (isReadOnly === true || isDisabled === true) {
                  return;
                }
                setIsPopoverOpen(true);
                // Select all text on click for easy searching
                e.currentTarget.select();
              }}
              onFocus={(e) => {
                if (isReadOnly === true || isDisabled === true) {
                  return;
                }
                // Select all text on focus for easy searching
                e.currentTarget.select();
              }}
              onBlur={(e) => {
                if (isReadOnly === true || isDisabled === true) {
                  return;
                }

                if (
                  e.relatedTarget?.classList.contains(
                    'ndl-timezone-picker-popover-item',
                  ) === true
                ) {
                  return;
                }

                // In UTC mode (utc or both), try to parse custom input on blur
                if (
                  (mode === 'utc' || mode === 'both') &&
                  inputValue.trim().toUpperCase().startsWith('UTC')
                ) {
                  const parsedOffset = parseCustomUTCOffset(inputValue.trim());
                  if (parsedOffset) {
                    handleOnClick(parsedOffset);
                    setError(undefined);
                    return;
                  }
                }

                // Reset to the current value if no valid selection was made
                if (value) {
                  setInputValue(formatTimeZone(value, referenceDate));
                } else {
                  setInputValue('');
                }
                setSearchQuery('');
                setError(undefined);
              }}
              placeholder={
                mode === 'city'
                  ? 'Select timezone'
                  : mode === 'utc'
                    ? 'Select or type UTC offset (e.g., UTC+5:30)'
                    : 'Select timezone or type UTC offset'
              }
              {...htmlAttributes}
              {...restProps}
            />
            <ChevronDownIcon className="ndl-icon-svg ndl-timezone-picker-icon" />
          </div>
        </label>
        {errorToShow !== undefined && (
          <div className="ndl-timezone-picker-error-wrapper" role="status">
            <ExclamationCircleIconSolid className="ndl-timezone-picker-error-icon" />
            <Typography
              variant={size === 'large' ? 'body-medium' : 'body-small'}
              className="ndl-timezone-picker-error-text"
              htmlAttributes={{ id: errorTextId }}
            >
              {errorToShow}
            </Typography>
          </div>
        )}
      </div>

      {context.open && (
        <ConditionalWrap
          shouldWrap={isPortaled}
          wrap={(wrapChildren) => (
            <FloatingPortal>{wrapChildren}</FloatingPortal>
          )}
        >
          <FloatingFocusManager
            context={context}
            modal={false}
            initialFocus={-1}
          >
            <div
              ref={refs.setFloating}
              className={classNames(
                themeClassName,
                'ndl-timezone-picker-popover',
                {
                  ...classes,
                },
              )}
              style={{
                ...transitionStyles,
                ...floatingStyles,
                width:
                  isFluid === true
                    ? containerRef.current?.clientWidth
                    : undefined,
              }}
              {...getFloatingProps()}
            >
              <ul ref={listRef} role="listbox" id={listboxId} tabIndex={-1}>
                {mode === 'both' ? (
                  <>
                    {utcOptions.length > 0 && (
                      <>
                        <li className="ndl-timezone-picker-section-header">
                          UTC Offsets
                        </li>
                        {utcOptions.map((option, i) => (
                          <li
                            id={`${listboxId}-option-${i}`}
                            key={option.value}
                            role="option"
                            aria-selected={value === option.value}
                            value={option.value}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleOnClick(option.value);
                              setIsPopoverOpen(false);
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                            }}
                            tabIndex={-1}
                            className={classNames(
                              focusedIndex === i ? 'focused' : '',
                              'ndl-timezone-picker-popover-item',
                            )}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.stopPropagation();
                                handleOnClick(option.value);
                              }
                            }}
                          >
                            {option.label}
                          </li>
                        ))}
                      </>
                    )}
                    {cityOptions.length > 0 && (
                      <>
                        <li className="ndl-timezone-picker-section-header">
                          City-Based Timezones
                        </li>
                        {cityOptions.map((option, i) => {
                          const adjustedIndex = i + utcOptions.length;
                          return (
                            <li
                              id={`${listboxId}-option-${adjustedIndex}`}
                              key={option.value}
                              role="option"
                              aria-selected={value === option.value}
                              value={option.value}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleOnClick(option.value);
                                setIsPopoverOpen(false);
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                              tabIndex={-1}
                              className={classNames(
                                focusedIndex === adjustedIndex ? 'focused' : '',
                                'ndl-timezone-picker-popover-item',
                              )}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.stopPropagation();
                                  handleOnClick(option.value);
                                }
                              }}
                            >
                              {option.label}
                            </li>
                          );
                        })}
                      </>
                    )}
                    {utcOptions.length === 0 && cityOptions.length === 0 && (
                      <li className="ndl-timezone-picker-popover-item ndl-timezone-picker-no-results">
                        No timezones found
                      </li>
                    )}
                  </>
                ) : (
                  <>
                    {filteredOptions.length > 0 ? (
                      filteredOptions.map((option, i) => (
                        <li
                          id={`${listboxId}-option-${i}`}
                          key={option.value}
                          role="option"
                          aria-selected={value === option.value}
                          value={option.value}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleOnClick(option.value);
                            setIsPopoverOpen(false);
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                          tabIndex={-1}
                          className={classNames(
                            focusedIndex === i ? 'focused' : '',
                            'ndl-timezone-picker-popover-item',
                          )}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.stopPropagation();
                              handleOnClick(option.value);
                            }
                          }}
                        >
                          {option.label}
                        </li>
                      ))
                    ) : (
                      <li className="ndl-timezone-picker-popover-item ndl-timezone-picker-no-results">
                        No timezones found
                      </li>
                    )}
                  </>
                )}
              </ul>
            </div>
          </FloatingFocusManager>
        </ConditionalWrap>
      )}
    </>
  );
};
